import { FC, useState } from 'react';

import { router, usePage } from '@inertiajs/react';

import { Button } from '../../../../components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../../components/ui/Dialog';
import { Routes } from '../../../../models/routes';
import { ConfirmModalState } from '../../../../types/ui';
import { withProfileOffersReturnTo } from '../__shared/returnContext';

interface Props {
    state: ConfirmModalState;
    setState: (state: ConfirmModalState) => void;
}

type PageProps = {
    returnTo?: string;
};

const ConfirmModalContent: FC<{
    payload: ConfirmModalState['payload'];
}> = ({ payload }) => {
    if (!payload) throw Error('Payload is Missing!');

    switch (payload.type) {
        case 'activate':
            return payload.quantity === 'single' ||
                payload.offersId.length === 1 ? (
                <span>Ви дійсно хочете активувати товар?</span>
            ) : (
                <span>
                    Ви дійсно хочете активувати товари (
                    {payload.offersId.length}) ?
                </span>
            );

        case 'deactivate':
            return payload.quantity === 'single' ||
                payload.offersId.length === 1 ? (
                <span>Ви дійсно хочете деактивувати товар?</span>
            ) : (
                <span>
                    Ви дійсно хочете деактивувати товари (
                    {payload.offersId.length}) ?
                </span>
            );

        case 'delete':
            return payload.quantity === 'single' ||
                payload.offersId.length === 1 ? (
                <span>Ви дійсно хочете видалити товар?</span>
            ) : (
                <span>
                    Ви дійсно хочете видалити товари ({payload.offersId.length})
                    ?
                </span>
            );
    }
};

const ConfirmModalActionLabel: FC<{
    payload: ConfirmModalState['payload'];
}> = ({ payload }) => {
    if (!payload) throw Error('Payload is Missing!');

    switch (payload?.type) {
        case 'activate':
            return <span>Активувати</span>;

        case 'deactivate':
            return <span>Деактивувати</span>;

        case 'delete':
            return <span>Видалити</span>;
    }
};
export const ConfirmModal: FC<Props> = ({ state, setState }) => {
    const { returnTo } = usePage<PageProps>().props;
    const [loading, setLoading] = useState(false);

    const onSuccess = () => {
        setState({ ...state, open: false });
        setLoading(false);
    };

    const submit = () => {
        if (!state.payload) return;

        setLoading(true);

        switch (state.payload.type) {
            case 'delete':
                if (state.payload.quantity === 'single') {
                    return router.delete(
                        withProfileOffersReturnTo(
                            `${Routes.Offers}/${state.payload.offerId}`,
                            returnTo,
                        ),
                        {
                            preserveScroll: true,
                            onSuccess,
                        },
                    );
                }

                return router.put(
                    `${Routes.Offers}/bulk_delete`,
                    { offer_ids: state.payload.offersId },
                    {
                        preserveScroll: true,
                        preserveState: false,
                        onSuccess,
                    },
                );

            case 'activate':
                if (state.payload.quantity === 'single') {
                    return router.put(
                        withProfileOffersReturnTo(
                            `${Routes.Offers}/${state.payload.offerId}/activate`,
                            returnTo,
                        ),
                        {},
                        { preserveScroll: true, onSuccess },
                    );
                }

                return router.put(
                    `${Routes.Offers}/bulk_update`,
                    {
                        offer_ids: state.payload.offersId,
                        offers: { action: state.payload.type },
                    },
                    {
                        preserveScroll: true,
                        preserveState: false,
                        onSuccess,
                    },
                );
            case 'deactivate':
                if (state.payload.quantity === 'single') {
                    return router.put(
                        withProfileOffersReturnTo(
                            `${Routes.Offers}/${state.payload.offerId}/deactivate`,
                            returnTo,
                        ),
                        {},
                        {
                            preserveScroll: true,
                            onSuccess,
                        },
                    );
                }

                return router.put(
                    `${Routes.Offers}/bulk_update`,
                    {
                        offer_ids: state.payload.offersId,
                        offers: { action: state.payload.type },
                    },
                    {
                        preserveScroll: true,
                        preserveState: false,
                        onSuccess,
                    },
                );
        }
    };

    return (
        <Dialog
            open={state.open}
            onOpenChange={(open) => {
                if (!loading) {
                    setState({ ...state, open });
                }
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Підтвердити операцію</DialogTitle>
                </DialogHeader>

                <ConfirmModalContent payload={state.payload} />

                <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0">
                    <Button
                        variant="outline"
                        onClick={() => setState({ ...state, open: false })}
                        className="mt-2 w-full sm:mt-0 sm:w-auto"
                        disabled={loading}
                    >
                        Скасувати
                    </Button>
                    <Button
                        onClick={submit}
                        className="w-full sm:w-auto"
                        disabled={loading}
                    >
                        <ConfirmModalActionLabel payload={state.payload} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
